<template>
  <div class="customizer">
    <div class="supernav-wrapper" v-if="tabCount > 1">
      <nav class="supernav" ref="supernav" @scroll.passive="supernavScroll">
        <div class="supernav-contents" ref="supernavContents" @transitionend="supernavTransitionEnd">
          <button
            type="button"
            class="supernav-link"
            v-for="tab in tabs"
            :key="tab.key"
            :aria-selected="currentTab === tab.key"
            @click="currentTab = tab.key">
            {{tab.value}}
          </button>
        </div>
      </nav>
      <button class="supernav-advancer supernav-advancer-left" type="button" @click="superClickLeft">
        <svg class="supernav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 551 1024"><path d="M445.44 38.183L-2.53 512l447.97 473.817 85.857-81.173-409.6-433.23v81.172l409.6-433.23L445.44 38.18z"/></svg>
      </button>
      <button class="supernav-advancer supernav-advancer-right" type="button" @click="superClickRight">
        <svg class="supernav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 551 1024"><path d="M105.56 985.817L553.53 512 105.56 38.183l-85.857 81.173 409.6 433.23v-81.172l-409.6 433.23 85.856 81.174z"/></svg>
      </button>
    </div>
    <div class="customizer-panel">
      <transition name="only-fade-in" mode="out-in">
        <div v-if="currentTab === 'basic'" key="basic">
          <slot></slot>
        </div>
        <FontCustomizer :category="category" :config="fontConfig" v-if="currentTab === 'font'" key="font" />
        <div class="row row-space-4" v-if="currentTab === 'border'" key="border">
          <div :class="{
              'col-md-6': !borderConfig.hideBorderSize,
              'col-md-12': borderConfig.hideBorderSize
            }">
            <TRBLCustomizer
              v-if="!borderConfig.hideBorderSize"
              label="Size"
              :category="category"
              prefix="border"
              suffix="Width"
              class="mb-4" />
            <TRBLCustomizer
              v-if="!borderConfig.hideBorderRadius"
              label="Radius"
              :category="category"
              prefix="border"
              suffix="Radius"
              cross
              :class="{
                'mb-4': !borderConfig.hideBorderSize
              }" />
           <ColorCustomizer
              v-if="!borderConfig.hideBorderColor"
              :category="category"
              label="Color"
              type="borderColor"
              class="mb-4" />
           <BorderStyleCustomizer
              v-if="!borderConfig.hideBorderStyle"
              :category="category" />
          </div>
          <div class="col-md-5 mx-auto" v-if="!borderConfig.hideBorderSize">
            <BoxModel />
          </div>
        </div>
        <div class="row row-space-4" v-if="currentTab === 'space'" key="space">
          <div class="col-md-6">
            <TRBLCustomizer
               v-if="!spaceConfig.hideMargin"
              label="Margin"
              :category="category"
              prefix="margin"
              class="mb-4" />
            <TRBLCustomizer
               v-if="!spaceConfig.hidePadding"
              label="Padding"
              :category="category"
              prefix="padding" />
          </div>
          <div class="col-md-5 mx-auto">
            <BoxModel />
          </div>
        </div>
        <AnimationCustomizer :category="animationConfig.category || category" v-if="currentTab === 'animation'" key="animation" />
      </transition>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'

import FontCustomizer from '@/components/customizers/FontCustomizer'
import ColorCustomizer from '@/components/customizers/ColorCustomizer'
import TRBLCustomizer from '@/components/customizers/TRBLCustomizer'
import BorderStyleCustomizer from '@/components/customizers/BorderStyleCustomizer'
import AnimationCustomizer from '@/components/customizers/AnimationCustomizer'
import BoxModel from '@/components/BoxModel'

import '@/assets/scss/_supernav.scss'
import '@/assets/scss/_superfonts.scss'

export default {
  components: {
    FontCustomizer,
    ColorCustomizer,
    TRBLCustomizer,
    BorderStyleCustomizer,
    AnimationCustomizer,
    BoxModel
  },
  props: {
    category: String,
    fontConfig: {
      type: Object,
      default: () => ({})
    },
    borderConfig: {
      type: Object,
      default: () => ({})
    },
    spaceConfig: {
      type: Object,
      default: () => ({})
    },
    animationConfig: {
      type: Object,
      default: () => ({})
    },

    basic: {
      type: Boolean,
      default: false
    },
    font: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: false
    },
    space: {
      type: Boolean,
      default: false
    },
    animation: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      tabState: null
    }
  },
  created: function () {
    this.navBarTravelling = false
    this.navBarTravelDirection = ''
    this.navBarTravelDistance = 100
    this.ticking = false
  },
  computed: {
    ...mapState([ 'logoAspectRatio' ]),
    tabs: function () {
      const tabs = []

      if (this.basic) {
        tabs.push({
          key: 'basic',
          value: 'Basic'
        })
      }

      if (this.animation) {
        tabs.push({
          key: 'animation',
          value: 'Animation'
        })
      }

      if (this.font) {
        tabs.push({
          key: 'font',
          value: 'Font'
        })
      }

      if (this.border) {
        tabs.push({
          key: 'border',
          value: 'Border'
        })
      }

      if (this.space) {
        tabs.push({
          key: 'space',
          value: 'Space'
        })
      }

      return tabs
    },
    currentTab: {
      get () {
        if (this.tabState) return this.tabState

        return this.tabs[0].key
      },
      set (value) {
        this.tabState = value
      }
    },
    tabCount: function () {
      return this.tabs.length
    }
  },
  mounted: function () {
    if (this.tabCount > 1) {
      const { supernavContents, supernav } = this.$refs
      supernav.setAttribute('data-overflowing', this.determineOverflow(supernavContents, supernav))
    }
  },
  methods: {
    determineOverflow: function (content, container) {
      const containerMetrics = container.getBoundingClientRect()
      const containerMetricsRight = Math.floor(containerMetrics.right)
      const containerMetricsLeft = Math.floor(containerMetrics.left)
      const contentMetrics = content.getBoundingClientRect()
      const contentMetricsRight = Math.floor(contentMetrics.right)
      const contentMetricsLeft = Math.floor(contentMetrics.left)
      if (containerMetricsLeft > contentMetricsLeft && (containerMetricsRight + 1 < contentMetricsRight)) {
        return 'both'
      } else if (contentMetricsLeft < containerMetricsLeft) {
        return 'left'
      } else if (contentMetricsRight > containerMetricsRight) {
        return 'right'
      } else {
        return 'none'
      }
    },
    superClickLeft: function () {
      if (this.navBarTravelling === true) {
        return
      }
      const { supernavContents, supernav } = this.$refs
      const overflow = this.determineOverflow(supernavContents, supernav)
      if (overflow === 'left' || overflow === 'both') {
        const availableScrollLeft = supernav.scrollLeft
        // If the space available is less than two lots of our desired distance, just move the whole amount
        // otherwise, move by the amount in the settings
        if (availableScrollLeft < this.navBarTravelDistance * 2) {
          supernavContents.style.transform = 'translateX(' + availableScrollLeft + 'px)'
        } else {
          supernavContents.style.transform = 'translateX(' + this.navBarTravelDistance + 'px)'
        }
        // We do want a transition (this is set in CSS) when moving so remove the class that would prevent that
        supernavContents.classList.remove('supernav-contents-no-transition')
        this.navBarTravelDirection = 'left'
        this.navBarTravelling = true
      }
      supernav.setAttribute('data-overflowing', overflow)
    },
    superClickRight: function () {
      if (this.navBarTravelling === true) {
        return
      }
      const { supernavContents, supernav } = this.$refs
      const overflow = this.determineOverflow(supernavContents, supernav)
      if (overflow === 'right' || overflow === 'both') {
        // Get the right edge of the container and content
        const navBarRightEdge = supernavContents.getBoundingClientRect().right
        const navBarScrollerRightEdge = supernav.getBoundingClientRect().right
        // Now we know how much space we have available to scroll
        const availableScrollRight = Math.floor(navBarRightEdge - navBarScrollerRightEdge)
        // If the space available is less than two lots of our desired distance, just move the whole amount
        // otherwise, move by the amount in the settings
        if (availableScrollRight < this.navBarTravelDistance * 2) {
          supernavContents.style.transform = 'translateX(-' + availableScrollRight + 'px)'
        } else {
          supernavContents.style.transform = 'translateX(-' + this.navBarTravelDistance + 'px)'
        }
        // We do want a transition (this is set in CSS) when moving so remove the class that would prevent that
        supernavContents.classList.remove('supernav-contents-no-transition')
        this.navBarTravelDirection = 'right'
        this.navBarTravelling = true
      }
      supernav.setAttribute('data-overflowing', overflow)
    },
    supernavScroll: function () {
      const { supernavContents, supernav } = this.$refs
      const ref = this
      // this.lastScrollingPosition = window.scrollY
      if (!this.ticking) {
        window.requestAnimationFrame(function () {
          const x = ref.determineOverflow(supernavContents, supernav)
          supernav.setAttribute('data-overflowing', x)
          ref.ticking = false
        })
      }
      this.ticking = true
    },
    supernavTransitionEnd: function () {
      const { supernavContents, supernav } = this.$refs
      const styleOfTransform = window.getComputedStyle(supernavContents, null)
      const tr = styleOfTransform.getPropertyValue('-webkit-transform') || styleOfTransform.getPropertyValue('transform')
      // If there is no transition we want to default to 0 and not null
      const amount = Math.abs(parseInt(tr.split(',')[4]) || 0)
      supernavContents.style.transform = 'none'
      supernavContents.classList.add('supernav-contents-no-transition')
      // Now lets set the scroll position
      if (this.navBarTravelDirection === 'left') {
        supernav.scrollLeft = supernav.scrollLeft - amount
      } else {
        supernav.scrollLeft = supernav.scrollLeft + amount
      }
      this.navBarTravelling = false
    }
  }
}
</script>
